import { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { cx, useOnElementResize } from '@trinity/chat-components';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import type { TabBarProps } from './interfaces';
import styles from './style.module.css';
import { TabBarTitle } from './TabBarTitle';
import TabIndicator from './TabIndicator';

type Props<T> = {
  className?: string;
  classes?: {
    root?: string;
    tab?: string;
    tabs?: string;
  };
} & TabBarProps<T>;


const TabBar = <T,>({ classes = {}, ...props }: Props<T>) => {

  function getClassName(key: string) {
    const i = props.routes.findIndex(r => r.key === key);
    return cx(props.index === i ? styles.active : styles.tab);
  }

  const ctx = useContext(AppThemingContext);

  // Helps us determine if the tab is a dashboard type
  // and apply specific styles if needed.
  const dashboardKeys = ['dashboard-displayr', 'dashboard-sigma', 'dashboard-tableau'];

  const getStyle = useCallback((key: string) => {
    const i = props.routes.findIndex(r => r.key === key);
    const color = ctx?.enabled && props.index === i ? ctx?.palette?.primary?.main : undefined;

    return { color };
  }, [
    ctx?.enabled,
    ctx?.palette,
    props.index,
    props.routes,
  ]);

  function handleClick(key: string) {
    const i = props.routes.findIndex(r => r.key === key);
    return () => props.onClick(i);
  }

  const [maxItems, setMaxItems] = useState(props.routes.length);
  const [itemContainer, setItemContainer] = useState<HTMLDivElement>();

  const recalculateMaxItems = useCallback(() => {
    if (itemContainer) {
      const parentSize = itemContainer.parentElement.clientWidth;
      const itemWidth = itemContainer.children[0].clientWidth;

      if (itemWidth * props.routes.length > parentSize) {
        setMaxItems(Math.floor(parentSize / itemWidth) - 1);
      } else {
        setMaxItems(props.routes.length);
      }
    }
  }, [itemContainer, props.routes.length]);

  useEffect(recalculateMaxItems, [itemContainer, recalculateMaxItems]);
  useOnElementResize({
    element: itemContainer,
    handler: recalculateMaxItems,
  });

  const zIndexMiddleware = useZIndexMiddleware();
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({ placement: 'bottom', middleware: [zIndexMiddleware], open, onOpenChange: setOpen });

  const orderedItems = useMemo(() => {
    if (props.index > maxItems - 1) {
      return arraymove(props.routes, props.index, maxItems - 1);
    } else {
      return props.routes;
    }
  }, [props.routes, props.index, maxItems]);

  const displayIndex = orderedItems.findIndex(i => i.key === props.routes[props.index].key);
  const showMore = maxItems < props.routes.length;

  return (
    <div className={cx(styles.root, classes.root)}>
      <div className={styles.wrap}>
        <div className={styles.scrollable}>
          <div ref={setItemContainer} className={cx(styles.tabs, classes.tabs, props.className)}>
            {orderedItems.slice(0, maxItems).map((item, i) =>
              <TabBarTitle
                className={cx(
                  getClassName(item.key),
                  classes.tab,
                  dashboardKeys.includes(item.key) && styles.dashboardTab,
                )}
                key={item.key}
                index={i}
                onClick={handleClick(item.key)}
                style={getStyle(item.key)}>
                {item.title}
              </TabBarTitle>,
            )}
            {showMore &&
              <>
                <TabBarTitle
                  className={cx(getClassName('more'), classes.tab)}
                  key={'more'}
                  index={-1}
                  onClick={() => setOpen(true)}>
                  <div ref={refs.setReference}>
                    More
                  </div>
                </TabBarTitle>
                <Popper
                  open={open}
                  ref={refs.setFloating}
                  style={floatingStyles}>
                  <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <div className={styles.moreMenu}>
                      {orderedItems.slice(maxItems).map((r, i) => (
                        <div
                          key={r.key}
                          className={styles.menuItem}
                          onClick={handleClick(r.key)}>
                          {r.title}
                        </div>
                      ))}
                    </div>
                  </ClickAwayListener>
                </Popper>
              </>}
            <TabIndicator
              count={maxItems + (showMore ? 1 : 0)}
              index={displayIndex} />
          </div>
        </div>
        <div className={styles.bar} />
      </div>
    </div>
  );
};

function arraymove<T>(arrParam: T[], fromIndex: number, toIndex: number) {
  const arr = [...arrParam];
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);

  return arr;
}

export { TabBar };
export default TabBar;